// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — value-encoded DASH releases, on the browser globe and the immersive one.
 *
 * The stream host publishes some streams as immutable releases rather
 * than a fixed MPD: the index row names a `latest.json` pointer, which
 * names a `release.json`, which names the MPD, the `.dsa`, and how the
 * frames encode their values (`valueEncoding`). Those frames are not
 * pictures: luma carries a measurement (`luma8-linear`, `luma8-log` or
 * `luma8-classified`), the bottom rows of every frame are a calibration
 * strip rather than map, and the colours come from a palette the
 * release declares.
 *
 * This module resolves a release and turns its encoding into what both
 * globes already know how to draw — a 256-entry palette indexed by the
 * raw luma code (the data-encoded branches of earthTileLayer and
 * photorealEarth) — plus the rectangle of the frame the map occupies,
 * and the exact decoder the value readout uses. overlayOptionsFromDataset
 * carries it all. `Dataset.renderEncoding` is left unset, so the analysis
 * tools (which read the whole frame and decode linearly) and the
 * generated colorbar stay off for these streams.
 *
 * The code layout is the publisher's, read from the file rather than
 * assumed: codes below `nodataThresholdCode` are no data (transparent,
 * so the real Earth shows through); `dataMinCode`..`dataMaxCode` span
 * the palette, with the codes between the threshold and `dataMinCode`
 * clamped to its bottom; and the value behind a code is linear or
 * logarithmic between `vmin` and `vmax`, or a class for a classified
 * stream. Transparency follows `presentation.alphaMode`.
 */

import type { ColorScaleStop, ReleaseColorScale } from '../types/color-scale'
import type { ReleaseAuxClass, ReleaseAuxiliary, ReleaseClass, ReleaseEncoding, ReleaseEncodingKind } from '../types/release-encoding'
import { paletteT, valueAtCode } from '../types/release-encoding'
import { parseReleaseAccuracy, type ReleaseAccuracy } from '../types/error-budget'
import { parseReleaseFidelity } from '../types/release-fidelity'
import { startFidelityLoads } from './valueFidelity'
import { COLOR_SCALE_LUT_SIZE } from '../types/color-scale'
import { axisEndMs, createFrameAxis } from './frameCalendar'

export type { ReleaseAuxClass, ReleaseAuxiliary, ReleaseClass, ReleaseEncoding, ReleaseEncodingKind } from '../types/release-encoding'

/** What a resolved release gives the loader. */
export interface ResolvedRelease {
  readonly mpdUrl: string
  readonly dsaUrl: string | null
  readonly encoding: ReleaseEncoding | null
  /** AYNI: the dates the release holds (end exclusive), or null when it does not say. */
  readonly timeRange: { readonly startMs: number; readonly endMs: number } | null
}

/**
 * The texture rectangle holding the map, as offset + scale in THREE's
 * UV space (v == 1 is the image's top row). `{ u0: 0, v0: 0, us: 1, vs: 1 }`
 * is the whole frame.
 */
export interface VrUvRegion {
  readonly u0: number
  readonly v0: number
  readonly us: number
  readonly vs: number
}

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

function rec(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function parseStops(raw: unknown): ColorScaleStop[] {
  if (!Array.isArray(raw)) return []
  const stops: ColorScaleStop[] = []
  for (const s of raw) {
    const r = rec(s)
    const t = num(r?.t)
    const rgba = r?.rgba
    if (t === null || t < 0 || t > 1 || !Array.isArray(rgba) || rgba.length !== 4) return []
    const c = rgba.map(num)
    if (c.some(v => v === null || v < 0 || v > 255)) return []
    stops.push({ t, rgba: [c[0]!, c[1]!, c[2]!, c[3]!] })
  }
  return stops.sort((a, b) => a.t - b.t)
}

const KINDS = new Set<ReleaseEncodingKind>(['luma8-linear', 'luma8-log', 'luma8-classified'])

/** AYNI: most auxiliary maps, and classes in one, a release may declare (AYNI 2D's limits). */
const MAX_AUXILIARY = 4
const MAX_AUX_CLASSES = 8

/**
 * AYNI — `valueEncoding.auxiliary`: classified maps in other rectangles of
 * the frame (IMERG's rain/snow "phase"). All or nothing, as in AYNI 2D
 * (`parseLumaAuxiliary`): one bad entry drops the block, because a partial
 * list would misnumber what is left; the values decode either way.
 */
function parseAuxiliary(
  raw: unknown,
  frame: { width: number; height: number },
  dataRegion: { x: number; y: number; width: number; height: number },
  nodataThresholdCode: number,
): ReleaseAuxiliary[] {
  if (raw === undefined || raw === null) return []
  if (!Array.isArray(raw) || raw.length > MAX_AUXILIARY) return []
  const out: ReleaseAuxiliary[] = []
  const overlaps = (a: { x: number; y: number; width: number; height: number }, b: typeof a) =>
    a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height
  for (const entry of raw) {
    const a = rec(entry)
    const name = typeof a?.name === 'string' ? a.name.trim() : ''
    if (!a || !name || out.some((o) => o.name === name) || a.kind !== 'luma8-classified') return []
    const r = rec(a.region)
    const region = { x: num(r?.x) ?? -1, y: num(r?.y) ?? -1, width: num(r?.width) ?? 0, height: num(r?.height) ?? 0 }
    if (region.x < 0 || region.y < 0 || region.width <= 0 || region.height <= 0 ||
        region.x + region.width > frame.width || region.y + region.height > frame.height ||
        overlaps(region, dataRegion) || out.some((o) => overlaps(region, o.region))) return []
    if (!Array.isArray(a.classes) || a.classes.length === 0 || a.classes.length > MAX_AUX_CLASSES) return []
    const classes: ReleaseAuxClass[] = []
    for (const c of a.classes) {
      const k = rec(c)
      const code = num(k?.code)
      const value = num(k?.value)
      if (!k || code === null || !Number.isInteger(code) || code < nodataThresholdCode || code > 255 || value === null ||
          typeof k.label !== 'string' || classes.some((x) => x.code === code)) return []
      let stops: ColorScaleStop[] | null = null
      if (k.palette !== undefined) {
        stops = parseStops(rec(k.palette)?.stops)
        if (stops.length < 2) return []
      }
      classes.push({ code, value, label: k.label, stops })
    }
    const def = a.defaultClass
    if (def !== undefined && def !== null && !(typeof def === 'number' && Number.isInteger(def) && def >= 0 && def < classes.length)) return []
    out.push({ name, region, classes, defaultClass: typeof def === 'number' ? def : null })
  }
  return out
}

/**
 * `valueEncoding` from a `release.json`, or null when the release is
 * not one this globe can decode — an unknown kind, a palette of fewer
 * than two stops, a degenerate code range, or a region outside the
 * frame. Null means "play it as a picture", never a guess.
 */
export function parseReleaseEncoding(release: unknown): ReleaseEncoding | null {
  const r = rec(release)
  const e = rec(r?.valueEncoding)
  const rep = rec(r?.representation)
  if (!e || !rep) return null
  const kind = e.kind
  if (typeof kind !== 'string' || !KINDS.has(kind as ReleaseEncodingKind)) return null

  const vmin = num(e.vmin)
  const vmax = num(e.vmax)
  const threshold = num(e.nodataThresholdCode)
  const lo = num(e.dataMinCode)
  const hi = num(e.dataMaxCode)
  const frameWidth = num(rep.width)
  const frameHeight = num(rep.height)
  const region = rec(e.dataRegion)
  const geo = rec(e.geo)
  const pres = rec(e.presentation)
  if (vmin === null || vmax === null || vmin === vmax) return null
  if (threshold === null || lo === null || hi === null || !(threshold <= lo && lo < hi && hi <= 255)) return null
  if (!frameWidth || !frameHeight || !region || !geo) return null
  if (kind === 'luma8-log' && !(vmin > 0 && vmax > 0)) return null

  const dataRegion = {
    x: num(region.x) ?? 0,
    y: num(region.y) ?? 0,
    width: num(region.width) ?? frameWidth,
    height: num(region.height) ?? frameHeight,
  }
  if (dataRegion.x < 0 || dataRegion.y < 0 || dataRegion.width <= 0 || dataRegion.height <= 0 ||
      dataRegion.x + dataRegion.width > frameWidth || dataRegion.y + dataRegion.height > frameHeight) {
    return null
  }
  const latTop = num(geo.latTop)
  const latBottom = num(geo.latBottom)
  const lonLeft = num(geo.lonLeft)
  const lonRight = num(geo.lonRight)
  if (latTop === null || latBottom === null || lonLeft === null || lonRight === null) return null

  const stops = parseStops(rec(pres?.defaultPalette)?.stops)
  if (stops.length < 2) return null

  const mode = pres?.alphaMode
  const alphaMode = mode === 'gradient' || mode === 'binary' || mode === 'opaque' ? mode : 'binary'
  const grad = rec(pres?.alphaGradient)
  const rampStart = num(grad?.rampStartValue)
  const rampEnd = num(grad?.rampEndValue)
  const classes: ReleaseClass[] = Array.isArray(e.classes)
    ? e.classes
        .map(rec)
        .filter((c): c is Record<string, unknown> => c !== null && num(c.code) !== null && num(c.value) !== null)
        .map(c => ({ code: num(c.code)!, value: num(c.value)!, label: typeof c.label === 'string' ? c.label : '' }))
        .sort((a, b) => a.code - b.code)
    : []

  return {
    kind: kind as ReleaseEncodingKind,
    units: typeof e.units === 'string' && e.units.trim() ? e.units : null,
    vmin,
    vmax,
    nodataThresholdCode: threshold,
    dataMinCode: lo,
    dataMaxCode: hi,
    dataRegion,
    frameWidth,
    frameHeight,
    geo: { latTop, latBottom, lonLeft, lonRight },
    stops,
    alphaMode,
    transparentBelowValue: num(pres?.transparentBelowValue),
    alphaGradient: rampStart !== null && rampEnd !== null && rampEnd > rampStart
      ? { rampStartValue: rampStart, rampEndValue: rampEnd, gamma: num(grad?.gamma) ?? 1 }
      : null,
    classes: kind === 'luma8-classified' ? classes : [],
    auxiliary: parseAuxiliary(e.auxiliary, { width: frameWidth, height: frameHeight }, dataRegion, threshold),
    ...accuracyOf(r),
    ...calibrationOf(e.calibration, frameHeight),
  }
}

/**
 * AYNI: the calibration strip, when it is well formed: `rows` rows at the
 * top or bottom of the frame, sampled on `sampleRow` of them, split into
 * one equal band per entry of `codes` (decoderRange.ts). Anything else:
 * none, and the codes are taken as published.
 */
function calibrationOf(raw: unknown, frameHeight: number): { calibration?: { row: number; codes: number[] } } {
  const c = rec(raw)
  const rows = num(c?.rows)
  const sampleRow = num(c?.sampleRow)
  const codes = Array.isArray(c?.codes) ? c.codes.map(num) : []
  if (rows === null || sampleRow === null || !Number.isInteger(rows) || !Number.isInteger(sampleRow)) return {}
  if (sampleRow < 0 || sampleRow >= rows || rows > frameHeight) return {}
  if (codes.length < 3 || codes.some((v) => v === null || !Number.isInteger(v) || v < 0 || v > 255)) return {}
  const bands = num(c?.bands)
  if (bands !== null && bands !== codes.length) return {}
  const row = c?.position === 'top' ? sampleRow : frameHeight - rows + sampleRow
  return { calibration: { row, codes: codes as number[] } }
}

/** AYNI: the error budget beside the encoding, when the release carries one (errorBudget.ts). */
function accuracyOf(release: Record<string, unknown> | null): { accuracy?: ReleaseAccuracy } {
  const accuracy = parseReleaseAccuracy(release)
  return accuracy ? { accuracy } : {}
}

// ---------------------------------------------------------------------------
// Decoding
// ---------------------------------------------------------------------------

// The decoder itself lives in `types/release-encoding.ts`, so the shared
// `lumaToValue` can decode a release exactly; re-exported here for callers.
export { paletteT, paletteTOfValue, valueAtCode, valueAtPaletteT } from '../types/release-encoding'

/** The colour at palette position `t` of a stop list (straight RGBA, 0–255). */
export function paletteAt(stops: readonly ColorScaleStop[], t: number): [number, number, number, number] {
  let i = 0
  while (i < stops.length - 2 && stops[i + 1]!.t < t) i++
  const a = stops[i]!
  const b = stops[i + 1] ?? a
  const span = b.t - a.t
  const f = span > 0 ? Math.min(1, Math.max(0, (t - a.t) / span)) : 0
  return [0, 1, 2, 3].map(c => a.rgba[c]! + (b.rgba[c]! - a.rgba[c]!) * f) as [number, number, number, number]
}

/** Opacity (0..1) the presentation gives a value, before the palette's own alpha. */
export function presentationAlpha(enc: ReleaseEncoding, value: number): number {
  // "Values below this render fully transparent", in every alpha mode, as
  // the desktop players apply it: sea ice concentration is `binary` with
  // a 15 % floor, and without the cut open ocean painted as dark-blue ice.
  // Not for a classified stream, whose values are class indices rather
  // than the physical units the floor is stated in.
  if (
    enc.kind !== 'luma8-classified' &&
    enc.transparentBelowValue !== null &&
    value < enc.transparentBelowValue
  ) return 0
  if (enc.alphaMode !== 'gradient') return 1
  const g = enc.alphaGradient
  if (!g) return 1
  const f = Math.min(1, Math.max(0, (value - g.rampStartValue) / (g.rampEndValue - g.rampStartValue)))
  return Math.pow(f, g.gamma)
}

/**
 * The exact 256-entry RGBA palette the release describes, indexed by
 * raw luma code — what the VR shader samples with the texture's `.r`.
 */
export function buildReleaseLut(enc: ReleaseEncoding): Uint8Array {
  const lut = new Uint8Array(COLOR_SCALE_LUT_SIZE * 4)
  for (let code = 0; code < COLOR_SCALE_LUT_SIZE; code++) {
    const value = valueAtCode(enc, code)
    if (value === null) continue // no data: fully transparent
    const rgba = paletteAt(enc.stops, paletteT(enc, code))
    const alpha = rgba[3] * presentationAlpha(enc, value)
    const o = code * 4
    lut[o] = Math.round(rgba[0])
    lut[o + 1] = Math.round(rgba[1])
    lut[o + 2] = Math.round(rgba[2])
    lut[o + 3] = Math.round(alpha)
  }
  return lut
}

/**
 * The same palette in the shape the VR globe consumes (`ColorScale`):
 * one stop per code at `t = code / 255`, with no `dataMinLuma` and no
 * `transparentRange`, so `buildColorScaleLut` reproduces
 * {@link buildReleaseLut} entry for entry and the existing
 * data-encoded branch draws it unchanged. `vmin` / `vmax` are the
 * values at codes 0 and 255 on the release's linear axis, so a linear
 * code→value reading stays right across the data codes.
 */
export function releaseColorScale(enc: ReleaseEncoding): ReleaseColorScale {
  const lut = buildReleaseLut(enc)
  const stops: ColorScaleStop[] = []
  const top = COLOR_SCALE_LUT_SIZE - 1
  for (let code = 0; code <= top; code++) {
    const o = code * 4
    stops.push({ t: code / top, rgba: [lut[o]!, lut[o + 1]!, lut[o + 2]!, lut[o + 3]!] })
  }
  const slope = (enc.vmax - enc.vmin) / (enc.dataMaxCode - enc.dataMinCode)
  const scale: ReleaseColorScale = {
    stops,
    vmin: enc.vmin - enc.dataMinCode * slope,
    vmax: enc.vmin + (top - enc.dataMinCode) * slope,
    // Records "no data below the threshold" for anything that asks the
    // scale (isTransparentLuma). buildColorScaleLut zeroes the same codes
    // the palette already made transparent, so the LUT is unchanged.
    transparentRange: enc.nodataThresholdCode / top,
    // The exact decoding, for whatever reads a value or recolours the
    // palette through the scale: `lumaToValue` decodes log and classified
    // releases with it, and the display transforms place the palette on
    // the data codes rather than on all 256 (colorScaleDisplay).
    release: enc,
  }
  if (enc.units) scale.units = enc.units
  return scale
}

/** Where the map sits in the frame in image space (v == 0 is the top row), as the browser globe and the value readout sample it. */
export function releaseCropRect(enc: ReleaseEncoding): { u0: number; v0: number; us: number; vs: number } {
  const { x, y, width, height } = enc.dataRegion
  return { u0: x / enc.frameWidth, v0: y / enc.frameHeight, us: width / enc.frameWidth, vs: height / enc.frameHeight }
}

/** Where the map sits in the frame, in THREE's flipped-Y UV space. */
export function releaseUvRegion(enc: ReleaseEncoding): VrUvRegion {
  const { x, y, width, height } = enc.dataRegion
  return {
    u0: x / enc.frameWidth,
    // Pixel rows count from the top; v counts from the bottom.
    v0: (enc.frameHeight - (y + height)) / enc.frameHeight,
    us: width / enc.frameWidth,
    vs: height / enc.frameHeight,
  }
}

/** A regional release as the overlay's bounding box; null for the whole globe. */
export function releaseBoundingBox(enc: ReleaseEncoding): { n: number; s: number; w: number; e: number } | null {
  const { latTop, latBottom, lonLeft, lonRight } = enc.geo
  const global = latTop >= 90 && latBottom <= -90 && lonRight - lonLeft >= 360
  return global ? null : { n: latTop, s: latBottom, w: lonLeft, e: lonRight }
}

/** Opaque white end to end: the colour carries nothing, the alpha ramp does. */
const WHITE_STOPS: readonly ColorScaleStop[] = [
  { t: 0, rgba: [255, 255, 255, 255] },
  { t: 1, rgba: [255, 255, 255, 255] },
]

/**
 * Published palettes known to be wrong, keyed by index dataset id and
 * drawn with a stand-in until the publisher fixes them (the same
 * override AYNI-2 carries). Only the colours are replaced: the
 * release's alpha mode, ramp, gamma and floor still apply.
 */
const PALETTE_OVERRIDES: Readonly<Record<string, readonly ColorScaleStop[]>> = {
  // Global Cloud Cover (NOAA SOS): the release palette runs white at 0 %
  // cover to black at 100 %, so thick cloud would draw black.
  de607183ec1dcc5f: WHITE_STOPS,
}

/** The encoding with its palette replaced when the dataset has a known-bad one. */
export function withPaletteOverride(datasetId: unknown, enc: ReleaseEncoding | null): ReleaseEncoding | null {
  const stops = typeof datasetId === 'string' ? PALETTE_OVERRIDES[datasetId] : undefined
  return enc && stops ? { ...enc, stops } : enc
}

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------

/**
 * Follow `latest.json` → `release.json` and return the playable MPD,
 * the release's `.dsa`, and its encoding. URLs inside each file are
 * resolved against that file's own URL, as the publisher writes them
 * relative. Throws with a message naming the step that failed, since a
 * release that cannot be resolved cannot be played at all.
 */
export async function resolveDashRelease(
  latestUrl: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ResolvedRelease> {
  const get = async (url: string, what: string): Promise<Record<string, unknown>> => {
    const res = await fetchImpl(url, { headers: { Accept: 'application/json' }, cache: 'no-cache' })
    if (!res.ok) throw new Error(`${what} ${url} answered ${res.status}`)
    const body = rec(await res.json())
    if (!body) throw new Error(`${what} ${url} is not a JSON object`)
    return body
  }
  const latest = await get(latestUrl, 'Release pointer')
  const pointer = rec(latest.releaseDescriptor)?.url
  if (typeof pointer !== 'string' || !pointer) throw new Error(`Release pointer ${latestUrl} names no release`)
  const releaseUrl = new URL(pointer, latestUrl).toString()
  const release = await get(releaseUrl, 'Release')
  const mpd = rec(release.mpd)?.url
  if (typeof mpd !== 'string' || !mpd) throw new Error(`Release ${releaseUrl} names no MPD`)
  const dsa = rec(release.dsa)?.url
  const parsed = parseReleaseEncoding(release)
  // AYNI: the release's error and source custody ride on its encoding (value error audit, 2026-10-19).
  const fidelity = parsed ? parseReleaseFidelity(release, releaseUrl) : null
  if (fidelity) startFidelityLoads(fidelity, fetchImpl)
  return {
    mpdUrl: new URL(mpd, releaseUrl).toString(),
    dsaUrl: typeof dsa === 'string' && dsa ? new URL(dsa, releaseUrl).toString() : null,
    encoding: withPaletteOverride(latest.datasetId ?? release.datasetId, parsed && fidelity ? { ...parsed, fidelity } : parsed),
    timeRange: parseReleaseTimeRange(release),
  }
}

/**
 * AYNI — the dates a release.json holds, end exclusive: from its first
 * frame to one step past its last (`timeTotalFrames` steps of
 * `timeCadenceSeconds`, calendar months where that is a month or a year:
 * frameCalendar.ts), the same end the stream's .dsa axis has. Null when it
 * names no start.
 */
export function parseReleaseTimeRange(release: unknown): { startMs: number; endMs: number } | null {
  const body = rec(release)
  const range = rec(body?.timeRange)
  const startMs = typeof range?.start === 'string' ? Date.parse(range.start) : NaN
  if (!Number.isFinite(startMs)) return null
  const frames = num(body?.timeTotalFrames)
  const cadenceS = num(body?.timeCadenceSeconds)
  const declaredEnd = typeof range?.end === 'string' ? Date.parse(range.end) : NaN
  // AYNI: on the frame calendar, as the .dsa's axis is (a monthly release
  // ends a calendar month after its last frame, not frames × mean month).
  const endMs = frames !== null && cadenceS !== null && frames > 0 && cadenceS > 0
    ? axisEndMs(createFrameAxis({ startMs, frameCount: frames, cadenceMs: cadenceS * 1000 }))
    : declaredEnd
  return Number.isFinite(endMs) && endMs > startMs ? { startMs, endMs } : null
}

/** How long a release's dates are trusted before they are asked for again (a live stream grows). */
const RANGE_TTL_MS = 10 * 60_000
const rangeCache = new Map<string, { at: number; range: Promise<{ startMs: number; endMs: number } | null> }>()

/**
 * AYNI — a release's dates, for the Real time list (which leaves out
 * streams with no dates in common with the dataset). Cached per pointer
 * for ten minutes; a release that cannot be read answers null, which the
 * list treats as "no dates to compare" and keeps.
 */
export function releaseTimeRange(latestUrl: string, fetchImpl: typeof fetch = fetch, now = Date.now()): Promise<{ startMs: number; endMs: number } | null> {
  const held = rangeCache.get(latestUrl)
  if (held && now - held.at < RANGE_TTL_MS) return held.range
  const range = resolveDashRelease(latestUrl, fetchImpl).then((r) => r.timeRange, () => null)
  rangeCache.set(latestUrl, { at: now, range })
  return range
}
